import { useCallback, useMemo, useState, useRef } from 'react';
import {
  PaginationState,
  TableOptions,
  SortingState,
  getSortedRowModel,
  getPaginationRowModel,
  RowSelectionState,
  ColumnFiltersState,
  VisibilityState,
  OnChangeFn,
} from '@tanstack/react-table';
import { CrudFilter } from './table-types';
import { DEFAULT_MIN_PAGE, getRowId } from './utils';

export const PAGE_SIZES = [5, 10, 20, 50, 100];

interface UseTableSettingsProps<T> {
  sorting?: boolean;
  clientSorting?: boolean;
  clientPagination?: boolean;
  minPage?: number;
  defaultPageSize?: number;
  defaultSortingState?: SortingState;
  onOriginalRowSelectionChange?: (data: T[]) => void;
  columnVisibility?: VisibilityState | null;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
}

const defaultProps: UseTableSettingsProps<unknown> = {
  sorting: true,
  clientSorting: false,
  clientPagination: false,
  defaultPageSize: 10,
  minPage: DEFAULT_MIN_PAGE,
};

export const pageSizeOptions = PAGE_SIZES.map(size => ({
  text: size,
  value: String(size),
}));

export enum FilterType {
  TEXT = 'text',
  SELECT = 'select',
  SINGLE_SELECT = 'single_select',
  NUMBER = 'number',
  DATE = 'date',
}

export const useTableSettings = <T>(
  {
    sorting: withSorting,
    clientSorting,
    clientPagination,
    minPage = DEFAULT_MIN_PAGE,
    defaultPageSize = 10,
    defaultSortingState = [],
    columnVisibility,
    onColumnVisibilityChange,
  }: UseTableSettingsProps<T> = defaultProps as UseTableSettingsProps<T>
) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: minPage,
    pageSize: defaultPageSize,
  });

  const [sorting, setSorting] = useState<SortingState>(defaultSortingState);
  const [filters, setFilters] = useState<CrudFilter[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const previousFiltersRef = useRef<CrudFilter[]>([]);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [originalRowSelection, setOriginalRowSelection] = useState<T[]>([]);

  const hasFiltersSet = filters.length > 0 || columnFilters.length > 0;

  const setFiltersWithComparison = useCallback(
    (newFilters: CrudFilter[]) => {
      const areEqual =
        JSON.stringify(newFilters) ===
        JSON.stringify(previousFiltersRef.current);

      if (!areEqual) {
        previousFiltersRef.current = newFilters;
        setFilters(newFilters);
        setPagination(p => ({ ...p, pageIndex: minPage }));
      }
    },
    [minPage]
  );

  const onColumnFiltersChangeWithReset: OnChangeFn<ColumnFiltersState> =
    useCallback(
      updater => {
        setColumnFilters(prev =>
          typeof updater === 'function' ? updater(prev) : updater
        );
        setPagination(p => ({ ...p, pageIndex: minPage }));
      },
      [minPage]
    );

  const onSortingChangeWithReset: OnChangeFn<SortingState> = useCallback(
    updater => {
      setSorting(prev =>
        typeof updater === 'function' ? updater(prev) : updater
      );
      setPagination(p => ({ ...p, pageIndex: minPage }));
    },
    [minPage]
  );

  const tableProps: Partial<TableOptions<T>> = useMemo(
    () => ({
      manualPagination: !clientPagination,
      onPaginationChange: setPagination,
      enableSorting: withSorting,
      manualSorting: withSorting && !clientSorting,
      state: {
        pagination,
        ...(withSorting && { sorting }),
        rowSelection,
        columnFilters,
        originalRowSelection,
        ...(columnVisibility && { columnVisibility }),
      },
      getRowId,
      onRowSelectionChange: setRowSelection,

      onColumnFiltersChange: onColumnFiltersChangeWithReset,
      ...(onColumnVisibilityChange && { onColumnVisibilityChange }),

      ...(clientPagination && {
        getPaginationRowModel: getPaginationRowModel<T>(),
      }),
      ...(withSorting && {
        onSortingChange: onSortingChangeWithReset,
        ...(clientSorting && { getSortedRowModel: getSortedRowModel<T>() }),
      }),
    }),
    [
      clientPagination,
      withSorting,
      clientSorting,
      pagination,
      sorting,
      rowSelection,
      originalRowSelection,
      columnFilters,
      columnVisibility,
      onColumnVisibilityChange,
      onColumnFiltersChangeWithReset,
      onSortingChangeWithReset,
    ]
  );

  const onLastElementDelete = useCallback(() => {
    if (pagination.pageIndex > 0) {
      setPagination(p => ({ ...p, pageIndex: p.pageIndex - 1 }));
    }
  }, [pagination.pageIndex]);

  return {
    sorting,
    tableProps,
    pageSizeOptions,
    pagination,
    minPage,
    filters,
    columnFilters,
    rowSelection,
    setRowSelection,
    hasFiltersSet,
    onLastElementDelete,
    onFiltersChange: setFiltersWithComparison,
    onColumnFiltersChange: onColumnFiltersChangeWithReset,
    onPaginationChange: setPagination,
    onSortingChange: onSortingChangeWithReset,
    onOriginalRowSelectionChange: setOriginalRowSelection,
  };
};
